"use client";

import { TextAreaField, TextField } from "@/components/tool-kit/primitives";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import type { NumberFormatter } from "@/lib/tool-kit/stats-tests/format";

/*
 * The k-group editor shared by the one-way ANOVA and Kruskal–Wallis calculators: a name
 * and a data box per group, plus add/remove buttons. Words arrive through `text`.
 */

export interface GroupInput {
  name: string;
  data: string;
}

export interface GroupFieldsText {
  /** "Group {n} name". */
  groupName: string;
  /** "Group {n} data — …". */
  groupData: string;
  /** "Group {n}". */
  groupDefault: string;
  dataPlaceholder: string;
  /** Plural message: "one: value|other: values". */
  valuesDetected: string;
  addGroup: string;
  removeGroup: string;
}

export const MIN_GROUPS = 2;
export const MAX_GROUPS = 10;
const SPLIT = "|";

export function defaultGroups(template: string, count: number): GroupInput[] {
  return Array.from({ length: count }, (_, i) => ({
    name: t(template, { n: i + 1 }),
    data: "",
  }));
}

/** Example groups from two `|`-separated strings: names, and data lists. */
export function exampleGroups(names: string, data: string): GroupInput[] {
  const labels = names.split(SPLIT);
  return data
    .split(SPLIT)
    .map((values, i) => ({ name: labels[i] ?? "", data: values.trim() }));
}

/** The non-empty groups' data and display names, in order. */
export function readGroups(
  groups: readonly GroupInput[],
  template: string
): { data: number[][]; names: string[] } {
  const data: number[][] = [];
  const names: string[] = [];
  for (const [i, group] of groups.entries()) {
    const values = parseNumberList(group.data);
    if (values.length === 0) {
      continue;
    }
    data.push(values);
    names.push(group.name.trim() || t(template, { n: i + 1 }));
  }
  return { data, names };
}

export function GroupFields({
  idPrefix,
  groups,
  onChange,
  text,
  locale,
  fmt,
}: {
  idPrefix: string;
  groups: readonly GroupInput[];
  onChange: (next: GroupInput[]) => void;
  text: GroupFieldsText;
  locale: string;
  fmt: NumberFormatter;
}) {
  const update = (index: number, next: GroupInput) =>
    onChange(groups.map((group, i) => (i === index ? next : group)));
  return (
    <div className="space-y-3">
      <div className="grid gap-4 sm:grid-cols-2">
        {groups.map((group, i) => {
          const count = parseNumberList(group.data).length;
          return (
            <div
              className="space-y-3 rounded-lg border border-border bg-card p-4"
              key={`${idPrefix}-group-${i + 1}`}
            >
              <TextField
                id={`${idPrefix}-name-${i + 1}`}
                label={t(text.groupName, { n: i + 1 })}
                onChange={(name) => update(i, { ...group, name })}
                value={group.name}
              />
              <TextAreaField
                hint={`${fmt(count)}${tPlural(text.valuesDetected, locale, count)}`}
                id={`${idPrefix}-data-${i + 1}`}
                label={t(text.groupData, { n: i + 1 })}
                onChange={(data) => update(i, { ...group, data })}
                placeholder={text.dataPlaceholder}
                rows={3}
                value={group.data}
              />
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          className={BUTTON_SMALL}
          disabled={groups.length >= MAX_GROUPS}
          onClick={() =>
            onChange([
              ...groups,
              {
                name: t(text.groupDefault, { n: groups.length + 1 }),
                data: "",
              },
            ])
          }
          type="button"
        >
          {text.addGroup}
        </button>
        <button
          className={BUTTON_SMALL}
          disabled={groups.length <= MIN_GROUPS}
          onClick={() => onChange(groups.slice(0, -1))}
          type="button"
        >
          {text.removeGroup}
        </button>
      </div>
    </div>
  );
}
